/**
 * 1.3.11: YUIChat 项目 - 引用来源面板组件
 * 展示AI回答的引用来源详情，显示相关度最高的文档片段
 * 1.6.9: 重构为 ChatMax 风格 - 固定右侧全高度面板，覆盖式展开
 * 1.6.11: 改为并排式布局 - 聊天面板缩小，引用面板展开，不遮挡
 * 1.6.14: 按文档分组显示 - 同一来源的引用合并到一个分组下
 */

import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import type { Citation } from '../types/chat';

// 1.6.11: 面板宽度常量（供外部使用）
// 1.6.15: 改为聊天区域的50%，使用 calc 动态计算
// 1.6.16: 改为函数，接受侧边栏宽度作为参数，支持侧边栏折叠（240px / 64px）
// 计算公式：(视口宽度 - 侧边栏宽度) / 2
export const getCitationPanelWidth = (sidebarWidth: number): string => {
  return `calc((100vw - ${sidebarWidth}px) / 2)`;
};
// 用于 margin 计算的近似值（1440px 屏幕时约 600px）
export const CITATION_PANEL_WIDTH = 600;

interface CitationPanelProps {
  citations: Citation[];
  isOpen: boolean;
  onClose: () => void;
  selectedCitationId?: string;
  onSelectCitation?: (id: string) => void;
  sidebarWidth?: number; // 1.6.16: 侧边栏宽度，用于动态计算面板宽度
}

// 1.6.14: 分组后的引用结构
interface GroupedCitations {
  source: string;
  displayName: string;
  isUrl: boolean;
  citations: Citation[];
}

/**
 * 1.3.11: 从 source 中提取显示名称
 * 如果是URL，返回完整URL；如果是文件路径，提取文件名
 */
function getSourceDisplayName(source: string): string {
  if (!source) return '未知来源';
  
  try {
    // 尝试解析为URL，返回完整URL
    new URL(source);
    return source;
  } catch {
    // 不是URL，尝试提取文件名
    const parts = source.split('/');
    return parts[parts.length - 1] || source;
  }
}

/**
 * 1.3.11: 判断是否为有效的URL
 */
function isValidUrl(source: string): boolean {
  if (!source) return false;
  try {
    new URL(source);
    return true;
  } catch {
    return false;
  }
}

/**
 * 1.6.14: 按来源分组引用
 */
function groupCitationsBySource(citations: Citation[]): GroupedCitations[] {
  const groups: Map<string, Citation[]> = new Map();
  
  citations.forEach(citation => {
    const source = citation.source || '未知来源';
    if (!groups.has(source)) {
      groups.set(source, []);
    }
    groups.get(source)!.push(citation);
  });
  
  return Array.from(groups.entries()).map(([source, citationList]) => ({
    source,
    displayName: getSourceDisplayName(source),
    isUrl: isValidUrl(source),
    citations: citationList
  }));
}

/**
 * 1.6.14: 单个段落项组件（可展开显示内容）
 */
function ParagraphItem({ 
  citation, 
  index,
  isSelected,
  onSelect
}: { 
  citation: Citation; 
  index: number;
  isSelected: boolean;
  onSelect: () => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation();
  
  return (
    <div
      className={`ml-4 border-l-2 ${isSelected ? 'border-primary' : 'border-gray-200'}`}
    >
      {/* 段落标题 */}
      <button
        onClick={() => {
          setIsExpanded(!isExpanded);
          onSelect();
        }}
        className="w-full pl-3 pr-2 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors text-left"
      >
        {/* 段落序号 */}
        <span className="flex-shrink-0 w-5 h-5 bg-primary/10 text-primary text-xs font-medium rounded flex items-center justify-center">
          {index + 1}
        </span>
        
        {/* 段落预览 */}
        <span className="flex-1 text-sm text-gray-600 truncate">
          {t('paragraph') || '段落'} {index + 1}
        </span>
        
        {/* 展开/收起图标 */}
        <span className="flex-shrink-0 text-gray-400">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4" />
          ) : (
            <ChevronRight className="w-4 h-4" />
          )}
        </span>
      </button>
      
      {/* 展开的内容 */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="pl-3 pr-2 pb-3 pt-1">
              <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap bg-gray-50 p-3 rounded-lg">
                {citation.content}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * 1.6.14: 文档分组组件
 */
function DocumentGroup({
  group,
  groupIndex,
  selectedCitationId,
  onSelectCitation
}: {
  group: GroupedCitations;
  groupIndex: number;
  selectedCitationId?: string;
  onSelectCitation?: (id: string) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(true); // 默认展开
  const { t } = useTranslation();
  
  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-sm">
      {/* 文档标题栏 */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-100"
      >
        {/* 文档序号徽章 */}
        <span className="flex-shrink-0 w-6 h-6 bg-primary text-white text-sm font-medium rounded-full flex items-center justify-center mt-0.5">
          {groupIndex + 1}
        </span>
        
        {/* 文档名称/URL */}
        <div className="flex-1 min-w-0">
          {group.isUrl ? (
            <a
              href={group.source}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-sm text-primary hover:underline break-all font-medium"
            >
              {group.displayName}
            </a>
          ) : (
            <span className="text-sm text-gray-700 break-all font-medium flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
              {group.displayName}
            </span>
          )}
          {/* 段落数量提示 */}
          <span className="text-xs text-gray-400 mt-1 block">
            {t('paragraphCount', { count: group.citations.length }) || `${group.citations.length} 个段落`}
          </span>
        </div>
        
        {/* 展开/收起图标 */}
        <span className="flex-shrink-0 text-gray-400 mt-0.5">
          {isExpanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </span>
      </button>
      
      {/* 段落列表 */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="py-2">
              {group.citations.map((citation, idx) => (
                <ParagraphItem
                  key={citation.id || `paragraph-${groupIndex}-${idx}`}
                  citation={citation}
                  index={idx}
                  isSelected={selectedCitationId === citation.id}
                  onSelect={() => onSelectCitation?.(citation.id)}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * 1.3.11: 引用来源面板主组件
 * 1.6.11: ChatMax 风格 - 并排式布局，与聊天面板同级
 * 1.6.14: 按文档分组显示引用
 */
export function CitationPanel({
  citations,
  isOpen,
  onClose,
  selectedCitationId,
  onSelectCitation,
  sidebarWidth = 240  // 1.6.16: 默认 240px（展开状态）
}: CitationPanelProps) {
  const { t } = useTranslation();
  
  // 1.6.14: 按来源分组引用
  const groupedCitations = useMemo(() => {
    return groupCitationsBySource(citations);
  }, [citations]);
  
  // 1.6.9: 面板打开时重置所有引用项的展开状态
  useEffect(() => {
    // 当面板打开时，可以在这里做一些初始化
  }, [isOpen, citations]);
  
  if (!isOpen || citations.length === 0) {
    return null;
  }
  
  // 1.6.13: 使用 fixed 定位实现全高度，同时聊天区域收缩（通过 ChatInterface 控制）
  // z-60 覆盖 ConversationHistorySidebar (z-50)
  return (
    <motion.div
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ 
        type: 'spring', 
        damping: 30, 
        stiffness: 300
      }}
      className="fixed top-0 right-0 bottom-0 bg-gray-50 flex flex-row border-l border-gray-200 z-[60]"
      style={{ width: getCitationPanelWidth(sidebarWidth) }}
    >
      {/* 1.6.9: 左侧收起按钮 - ChatMax 风格 */}
      <button
        onClick={onClose}
        className="flex-shrink-0 w-8 flex items-center justify-center bg-white border-r border-gray-200 hover:bg-gray-50 transition-colors"
        title={t('closeCitationPanel') || '关闭引用面板'}
      >
        <ChevronRight className="w-5 h-5 text-gray-400" />
      </button>
      
      {/* 右侧内容区域 */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center px-4 py-3 border-b border-gray-200 bg-white">
          <h3 className="text-sm font-semibold text-gray-800">
            {t('citationSources') || '引用来源'}
          </h3>
        </div>
        
        {/* 引用列表 - 1.6.14: 按文档分组显示 */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {groupedCitations.map((group, groupIndex) => (
            <DocumentGroup
              key={group.source || `group-${groupIndex}`}
              group={group}
              groupIndex={groupIndex}
              selectedCitationId={selectedCitationId}
              onSelectCitation={onSelectCitation}
            />
          ))}
          
          {/* 底部信息 - 移到滚动区域内 */}
          <div className="pt-3 border-t border-gray-200">
            <p className="text-xs text-gray-400">
              {t('citationNote') || `显示相关度最高的文档片段`}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default CitationPanel;
